import {
    Box,
    Button,
    Chip,
    Dialog,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@material-ui/core"
import { makeStyles } from "@material-ui/core/styles"
import { Assessment, Close, GetApp, Print, Search, Visibility } from "@material-ui/icons"
import React, { useEffect, useState } from "react"
import { useHistory } from "react-router-dom"
import { baixarCsv, csvLinha } from "../../../functions/exportarCsv"
import type { CaixaResumo, PagamentoResumo } from "../../../services/historicoCaixa"
import { imprimirFechamentoCaixa } from "../../../services/historicoCaixa"
import { gerarTextoFechamento } from "../../../services/textoFechamento"
import useAppStore from "../../../stores/AppStore"
import { useSnackbar } from "../../../utils/notificacoes"
import { useHistoricoCaixaContext } from "../context/useHistoricoCaixaContext"

const useStyles = makeStyles(theme => ({
    container: {
        paddingLeft: "90px",
        paddingRight: "10px",
        paddingTop: "100px",
        paddingBottom: "30px",
        [theme.breakpoints.down("md")]: {
            paddingLeft: "20px",
            paddingRight: "20px",
            paddingTop: "80px",
        },
        [theme.breakpoints.down("sm")]: {
            paddingLeft: "15px",
            paddingRight: "15px",
            paddingTop: "70px",
        },
    },
    content: {
        margin: "auto",
        maxWidth: "1280px",
        [theme.breakpoints.down("sm")]: { maxWidth: "100%" },
    },
    titleText: {
        fontWeight: 600,
        color: "#2c3e50",
        [theme.breakpoints.down("sm")]: { fontSize: "1.5rem" },
        [theme.breakpoints.down("xs")]: { fontSize: "1.25rem" },
    },
    configCard: {
        borderRadius: 20,
        boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        backgroundColor: "white",
        overflow: "hidden",
        marginBottom: theme.spacing(3),
        [theme.breakpoints.down("sm")]: { borderRadius: 15, marginBottom: theme.spacing(2) },
    },
    cardHeader: {
        backgroundColor: "#f8f9fa",
        padding: theme.spacing(2.5),
        borderBottom: "2px solid #e9ecef",
        display: "flex",
        alignItems: "center",
        [theme.breakpoints.down("sm")]: { padding: theme.spacing(2) },
    },
    cardHeaderTitle: {
        fontWeight: 600,
        color: "#2c3e50",
        [theme.breakpoints.down("sm")]: { fontSize: "1.1rem" },
    },
    cardContent: {
        padding: theme.spacing(3),
        [theme.breakpoints.down("sm")]: { padding: theme.spacing(2) },
    },
    iconHeader: {
        color: "#4caf50",
        marginRight: theme.spacing(1),
    },
    filterRow: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(2),
        marginBottom: theme.spacing(2),
        flexWrap: "wrap",
    },
    nomeCell: {
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
    },
    statusAberto: {
        backgroundColor: "#e8f5e9",
        color: "#2e7d32",
        fontWeight: 600,
    },
    statusFechado: {
        backgroundColor: "#f5f5f5",
        color: "#757575",
        fontWeight: 600,
    },
    resumoCard: {
        borderRadius: 12,
        padding: theme.spacing(2),
        textAlign: "center",
    },
    resumoValor: {
        fontWeight: 700,
        fontSize: "1.2rem",
    },
    resumoLabel: {
        color: "#7f8c8d",
        fontSize: "0.75rem",
    },
    pagamentoValor: {
        fontWeight: 600,
        textAlign: "right",
        display: "block",
    },
}))

function formatarData(millis: number | null): string {
    if (!millis) return "—"
    return new Date(millis).toLocaleString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    })
}

function formatarMoeda(valor: string | null | undefined): string {
    if (!valor) return "R$ 0,00"
    const num = parseFloat(valor)
    return num.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })
}

type DialogResumoProps = {
    caixa: CaixaResumo | null
    onClose: () => void
}

function DialogResumo({ caixa, onClose }: DialogResumoProps) {
    const classes = useStyles()
    if (!caixa) return null

    return (
        <Dialog open={!!caixa} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6" style={{ fontWeight: 600 }}>
                        Resumo do Caixa #{caixa.id}
                    </Typography>
                    <IconButton size="small" onClick={onClose}>
                        <Close />
                    </IconButton>
                </Box>
                <Typography variant="caption" style={{ color: "#7f8c8d" }}>
                    Consolidação por forma de pagamento
                </Typography>
            </DialogTitle>
            <DialogContent>
                <Grid container spacing={2} style={{ marginBottom: 16 }}>
                    <Grid item xs={4}>
                        <Paper
                            className={classes.resumoCard}
                            style={{ border: "1px solid #1976d2" }}
                        >
                            <Typography className={classes.resumoLabel}>
                                Dinheiro Inicial
                            </Typography>
                            <Typography className={classes.resumoValor}>
                                {formatarMoeda(caixa.dinheiroInicial)}
                            </Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={4}>
                        <Paper
                            className={classes.resumoCard}
                            style={{ border: "1px solid #e53935" }}
                        >
                            <Typography className={classes.resumoLabel}>Retiradas</Typography>
                            <Typography
                                className={classes.resumoValor}
                                style={{ color: "#c62828" }}
                            >
                                {formatarMoeda(caixa.somaRetiradasDoCaixa)}
                            </Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={4}>
                        <Paper
                            className={classes.resumoCard}
                            style={{ border: "1px solid #43a047" }}
                        >
                            <Typography className={classes.resumoLabel}>Total Vendas</Typography>
                            <Typography
                                className={classes.resumoValor}
                                style={{ color: "#2e7d32" }}
                            >
                                {formatarMoeda(caixa.somaVenda)}
                            </Typography>
                        </Paper>
                    </Grid>
                </Grid>

                <Paper
                    variant="outlined"
                    style={{ padding: 16, marginBottom: 16, borderRadius: 10 }}
                >
                    <Grid container spacing={1}>
                        <Grid item xs={6} sm={4}>
                            <Typography variant="caption" style={{ color: "#7f8c8d" }}>
                                Dinheiro
                            </Typography>
                            <Typography variant="body2" style={{ fontWeight: 600 }}>
                                {formatarMoeda(caixa.somaVendaDinheiro)}
                            </Typography>
                        </Grid>
                        <Grid item xs={6} sm={4}>
                            <Typography variant="caption" style={{ color: "#7f8c8d" }}>
                                Cartão
                            </Typography>
                            <Typography variant="body2" style={{ fontWeight: 600 }}>
                                {formatarMoeda(caixa.somaVendaCartao)}
                            </Typography>
                        </Grid>
                        <Grid item xs={6} sm={4}>
                            <Typography variant="caption" style={{ color: "#7f8c8d" }}>
                                iFood Online
                            </Typography>
                            <Typography variant="body2" style={{ fontWeight: 600 }}>
                                {formatarMoeda(caixa.somaVendaiFoodOnline)}
                            </Typography>
                        </Grid>
                        <Grid item xs={6} sm={4}>
                            <Typography variant="caption" style={{ color: "#7f8c8d" }}>
                                Pix Online
                            </Typography>
                            <Typography variant="body2" style={{ fontWeight: 600 }}>
                                {formatarMoeda(caixa.somaVendaPixOnline)}
                            </Typography>
                        </Grid>
                        <Grid item xs={6} sm={4}>
                            <Typography variant="caption" style={{ color: "#7f8c8d" }}>
                                Cartão - PagSeguro
                            </Typography>
                            <Typography variant="body2" style={{ fontWeight: 600 }}>
                                {formatarMoeda(caixa.somaVendaPagSeguroOnline)}
                            </Typography>
                        </Grid>
                        <Grid item xs={6} sm={4}>
                            <Typography variant="caption" style={{ color: "#7f8c8d" }}>
                                Outros
                            </Typography>
                            <Typography variant="body2" style={{ fontWeight: 600 }}>
                                {formatarMoeda(caixa.somaVendaOutros)}
                            </Typography>
                        </Grid>
                    </Grid>
                </Paper>

                <Divider style={{ marginBottom: 16 }} />

                <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: 8 }}>
                    Detalhamento por Forma de Pagamento
                </Typography>
                {caixa.pagamentos.length === 0 ? (
                    <Typography variant="body2" style={{ color: "#7f8c8d" }}>
                        Nenhum pagamento registrado.
                    </Typography>
                ) : (
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>
                                        <strong>Forma de Pagamento</strong>
                                    </TableCell>
                                    <TableCell align="right">
                                        <strong>Valor</strong>
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {caixa.pagamentos.map((p: PagamentoResumo, i: number) => (
                                    <TableRow key={i}>
                                        <TableCell>{p.descricao}</TableCell>
                                        <TableCell align="right">
                                            <span className={classes.pagamentoValor}>
                                                {p.total.toLocaleString("pt-BR", {
                                                    style: "currency",
                                                    currency: "BRL",
                                                })}
                                            </span>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </DialogContent>
        </Dialog>
    )
}

export function TabelaHistoricoCaixa() {
    const classes = useStyles()
    const history = useHistory()
    const { dados, erroCarregamento, dataInicio, dataFim, setDataInicio, setDataFim, recarregar } =
        useHistoricoCaixaContext()
    const { enqueueSnackbar } = useSnackbar()
    const [caixaSelecionado, setCaixaSelecionado] = useState<CaixaResumo | null>(null)
    const [imprimindoId, setImprimindoId] = useState<number | null>(null)

    const autorizacao = localStorage.getItem("chaveSistema") || ""
    const mandarImprimir = useAppStore(s => s.mandarImprimir)
    const configuracoes = useAppStore(s => s.configuracoes)
    const idEstabelecimento = configuracoes?.user?.parametros?.estabelecimento?.id

    useEffect(() => {
        if (erroCarregamento) {
            enqueueSnackbar(erroCarregamento, { variant: "error" })
        }
    }, [erroCarregamento])

    const handleBuscar = () => {
        if (dataInicio && dataFim && dataInicio > dataFim) {
            enqueueSnackbar("A data inicial não pode ser maior que a final.", { variant: "warning" })
            return
        }
        recarregar(dataInicio || undefined, dataFim || undefined)
    }

    // Exporta o período em CSV com o detalhamento de cada caixa (resumo por forma de pagamento
    // e formas registradas). Os dados já vêm completos na listagem, sem chamadas extras.
    const handleExportar = () => {
        if (dados.caixas.length === 0) {
            enqueueSnackbar("Não há caixas no período para exportar.", { variant: "info" })
            return
        }
        const linhas: string[] = [
            csvLinha([
                "ID",
                "Abertura",
                "Fechamento",
                "Status",
                "Dinheiro Inicial",
                "Retiradas",
                "Total Vendas",
                "Dinheiro",
                "Cartão",
                "iFood Online",
                "Pix Online",
                "Cartão PagSeguro",
                "Outros",
                "Qtd Pedidos",
                "Total Fechamento",
                "Quebra",
                "Formas de Pagamento",
            ]),
        ]
        for (const c of dados.caixas) {
            const formas = c.pagamentos
                .map(p => `${p.descricao}: ${formatarMoeda(String(p.total))}`)
                .join(" | ")
            linhas.push(
                csvLinha([
                    c.id,
                    formatarData(c.abertoEm),
                    formatarData(c.fechadoEm),
                    c.statusFuncionamento === 1 ? "Aberto" : "Fechado",
                    formatarMoeda(c.dinheiroInicial),
                    formatarMoeda(c.somaRetiradasDoCaixa),
                    formatarMoeda(c.somaVenda),
                    formatarMoeda(c.somaVendaDinheiro),
                    formatarMoeda(c.somaVendaCartao),
                    formatarMoeda(c.somaVendaiFoodOnline),
                    formatarMoeda(c.somaVendaPixOnline),
                    formatarMoeda(c.somaVendaPagSeguroOnline),
                    formatarMoeda(c.somaVendaOutros),
                    c.quantidadePedidos,
                    c.totalFechamentoCaixa ? formatarMoeda(c.totalFechamentoCaixa) : "",
                    c.quebraCaixa ? formatarMoeda(c.quebraCaixa) : "",
                    formas,
                ])
            )
        }
        baixarCsv(`historico-caixa_${dataInicio}_a_${dataFim}.csv`, linhas)
    }

    // Busca o fechamento estruturado no servidor e envia o texto (mesmo formato da tela de
    // Caixa) para a impressora, via o util compartilhado gerarTextoFechamento.
    const handleImprimirFechamento = async (caixa: CaixaResumo) => {
        if (!caixa?.id) return
        try {
            setImprimindoId(caixa.id)
            const resp = await imprimirFechamentoCaixa(autorizacao, caixa.id, idEstabelecimento)
            if ("retornoErro" in resp) {
                enqueueSnackbar(
                    resp.mensagem || "Não foi possível obter o fechamento do servidor.",
                    { variant: "error" }
                )
                return
            }
            const largura = configuracoes?.user?.parametros?.colunasPorLinhaImpressao
            const texto = "\n\n" + gerarTextoFechamento(resp, largura)
            mandarImprimir({ texto, numeroDaImpressora: 1, isResumo: true })
        } catch (error) {
            enqueueSnackbar("Erro ao imprimir o fechamento.", { variant: "error" })
        } finally {
            setImprimindoId(null)
        }
    }

    return (
        <Box className={classes.container}>
            <Box className={classes.content}>
                <Box mb={3}>
                    <Typography variant="h5" className={classes.titleText}>
                        Histórico do Caixa
                    </Typography>
                    <Typography variant="body2" style={{ color: "#7f8c8d", marginTop: 4 }}>
                        Aqui você consegue visualizar o histórico do caixa.
                    </Typography>
                </Box>

                <Paper className={classes.configCard}>
                    <Box className={classes.cardHeader}>
                        <Assessment className={classes.iconHeader} />
                        <Typography variant="h6" className={classes.cardHeaderTitle}>
                            Caixas
                        </Typography>
                    </Box>
                    <Box className={classes.cardContent}>
                        <Box className={classes.filterRow}>
                            <TextField
                                label="Início"
                                type="date"
                                variant="outlined"
                                size="small"
                                value={dataInicio}
                                onChange={e => setDataInicio(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                style={{ minWidth: 160 }}
                            />
                            <TextField
                                label="Fim"
                                type="date"
                                variant="outlined"
                                size="small"
                                value={dataFim}
                                onChange={e => setDataFim(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                style={{ minWidth: 160 }}
                            />
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<Search />}
                                onClick={handleBuscar}
                                style={{ borderRadius: 25 }}
                            >
                                Buscar
                            </Button>
                            <Button
                                variant="outlined"
                                color="primary"
                                startIcon={<GetApp />}
                                onClick={handleExportar}
                                disabled={dados.caixas.length === 0}
                                style={{ borderRadius: 25 }}
                            >
                                Exportar
                            </Button>
                        </Box>

                        {dados.caixas.length === 0 ? (
                            <Typography variant="body2" style={{ color: "#7f8c8d" }}>
                                Nenhum caixa encontrado.
                            </Typography>
                        ) : (
                            <TableContainer style={{ overflowX: "auto" }}>
                                <Table
                                    size="small"
                                    stickyHeader
                                    style={{ width: "100%", minWidth: 1030 }}
                                >
                                    <TableHead>
                                        <TableRow>
                                            <TableCell style={{ minWidth: 80 }}>ID</TableCell>
                                            <TableCell style={{ minWidth: 120 }}>
                                                Abertura
                                            </TableCell>
                                            <TableCell style={{ minWidth: 120 }}>
                                                Fechamento
                                            </TableCell>
                                            <TableCell style={{ minWidth: 100 }}>
                                                D. Inicial
                                            </TableCell>
                                            <TableCell style={{ minWidth: 100 }}>Total</TableCell>
                                            <TableCell style={{ minWidth: 100 }}>Quebra</TableCell>
                                            <TableCell style={{ minWidth: 90 }}>Pedidos</TableCell>
                                            <TableCell style={{ minWidth: 120 }}>Status</TableCell>
                                            <TableCell style={{ width: 200, minWidth: 200 }}>
                                                Ações
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {dados.caixas.map((caixa: CaixaResumo) => (
                                            <TableRow key={caixa.id} hover>
                                                <TableCell>{caixa.id}</TableCell>
                                                <TableCell>
                                                    {formatarData(caixa.abertoEm)}
                                                </TableCell>
                                                <TableCell>
                                                    {formatarData(caixa.fechadoEm)}
                                                </TableCell>
                                                <TableCell>
                                                    {formatarMoeda(caixa.dinheiroInicial)}
                                                </TableCell>
                                                <TableCell>
                                                    {caixa.totalFechamentoCaixa
                                                        ? formatarMoeda(caixa.totalFechamentoCaixa)
                                                        : "—"}
                                                </TableCell>
                                                <TableCell>
                                                    {caixa.quebraCaixa
                                                        ? formatarMoeda(caixa.quebraCaixa)
                                                        : "—"}
                                                </TableCell>
                                                <TableCell>{caixa.quantidadePedidos}</TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={
                                                            caixa.statusFuncionamento === 1
                                                                ? "Aberto"
                                                                : "Fechado"
                                                        }
                                                        size="small"
                                                        className={
                                                            caixa.statusFuncionamento === 1
                                                                ? classes.statusAberto
                                                                : classes.statusFechado
                                                        }
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Box
                                                        display="flex"
                                                        flexDirection="column"
                                                        style={{ gap: 6 }}
                                                    >
                                                        <Button
                                                            variant="outlined"
                                                            size="small"
                                                            color="primary"
                                                            onClick={() =>
                                                                setCaixaSelecionado(caixa)
                                                            }
                                                            style={{
                                                                borderRadius: 20,
                                                                fontSize: "0.7rem",
                                                            }}
                                                        >
                                                            Ver arrecadação
                                                        </Button>
                                                        {/* Abre a visão completa do caixa (mesma
                                                            da tela de Caixa aberta) em modo
                                                            somente-leitura, via ?caixa=<id>. */}
                                                        <Button
                                                            variant="outlined"
                                                            size="small"
                                                            startIcon={
                                                                <Visibility
                                                                    style={{ fontSize: 16 }}
                                                                />
                                                            }
                                                            onClick={() =>
                                                                history.push(
                                                                    `/servicos/modulo/caixa?caixa=${caixa.id}`
                                                                )
                                                            }
                                                            style={{
                                                                borderRadius: 20,
                                                                fontSize: "0.7rem",
                                                            }}
                                                        >
                                                            Visualizar caixa
                                                        </Button>
                                                        <Button
                                                            variant="contained"
                                                            size="small"
                                                            startIcon={
                                                                <Print style={{ fontSize: 16 }} />
                                                            }
                                                            disabled={imprimindoId === caixa.id}
                                                            onClick={() =>
                                                                handleImprimirFechamento(caixa)
                                                            }
                                                            style={{
                                                                borderRadius: 20,
                                                                fontSize: "0.68rem",
                                                                backgroundColor:
                                                                    imprimindoId === caixa.id
                                                                        ? undefined
                                                                        : "#0a7d2c",
                                                                color:
                                                                    imprimindoId === caixa.id
                                                                        ? undefined
                                                                        : "white",
                                                            }}
                                                        >
                                                            {imprimindoId === caixa.id
                                                                ? "Imprimindo..."
                                                                : "Imprimir Fechamento"}
                                                        </Button>
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </Box>
                </Paper>
            </Box>

            <DialogResumo caixa={caixaSelecionado} onClose={() => setCaixaSelecionado(null)} />
        </Box>
    )
}
